import type { GameState, Rock, Viewport } from "../sim/types";
import { depthOf, rockRadius, scaleOf } from "../sim/geometry";
import { C, FONT, roundRect, setTracking } from "./palette";
import { ri } from "../sim/rng";

/**
 * Un asteroide se dibuja en seis pasadas para que tenga volumen:
 *   1. cuerpo con gradiente radial desplazado (luz desde arriba-izquierda)
 *   2. terminador lineal que oscurece la cara opuesta
 *   3. facetas internas
 *   4. crateres como hoyos oscuros con arco iluminado en el borde
 *   5. silueta con destello
 *   6. luz de canto sobre el borde superior
 *
 * El relleno oscuro tambien sirve para que la roca tape el fondo.
 */

function rockPath(cx: CanvasRenderingContext2D, verts: number[], R: number): void {
  cx.beginPath();
  for (let i = 0; i < verts.length; i++) {
    const a = (i / verts.length) * Math.PI * 2;
    const rad = R * verts[i]!;
    const px = Math.cos(a) * rad;
    const py = Math.sin(a) * rad;
    if (i) cx.lineTo(px, py);
    else cx.moveTo(px, py);
  }
  cx.closePath();
}

export function drawRock(
  cx: CanvasRenderingContext2D,
  r: Rock,
  s: GameState,
  now: number,
  vp: Viewport,
): void {
  const p = depthOf(r.y, vp);
  const sc = scaleOf(p, s.depth);
  const R = rockRadius(r.band, r.stages, sc);
  const isCand = s.buffer.length > 0 && String(r.ans).startsWith(s.buffer);
  const chipping = r.chip > 0;
  const rim = r.stages > 1 ? C.accent : p > 0.86 ? C.danger : C.rock;
  const baseH = r.stages > 1 ? 36 : 150; // las de varias etapas tiran a ambar

  cx.save();
  cx.translate(r.x + (r.jolt > 0 ? (Math.random() - 0.5) * 4 * r.jolt : 0), r.y);
  cx.rotate(r.rot);
  cx.globalAlpha = chipping
    ? 0.5 + 0.5 * Math.abs(Math.sin(now * 0.05))
    : 0.5 + 0.5 * Math.min(1, p * 1.4);

  // 1. cuerpo iluminado
  rockPath(cx, r.verts, R);
  const g = cx.createRadialGradient(-R * 0.38, -R * 0.44, R * 0.08, 0, 0, R * 1.22);
  g.addColorStop(0, `hsla(${baseH},30%,${r.stages > 1 ? 56 : 48}%,0.95)`);
  g.addColorStop(0.42, `hsla(${baseH},26%,26%,0.95)`);
  g.addColorStop(0.78, `hsla(${baseH},24%,13%,0.97)`);
  g.addColorStop(1, `hsla(${baseH},22%,6%,1)`);
  cx.fillStyle = g;
  cx.fill();

  // 2. terminador
  const t = cx.createLinearGradient(-R * 0.2, -R * 0.2, R, R * 0.9);
  t.addColorStop(0, "rgba(0,0,0,0)");
  t.addColorStop(1, "rgba(0,0,0,0.62)");
  cx.fillStyle = t;
  cx.fill();

  // 3. facetas
  cx.strokeStyle = "rgba(0,0,0,0.42)";
  cx.lineWidth = 1;
  for (let i = 0; i < 3; i++) {
    const a1 = i * 2.1 + 0.6;
    const a2 = a1 + 2.0;
    cx.beginPath();
    cx.moveTo(Math.cos(a1) * R * 0.86, Math.sin(a1) * R * 0.86);
    cx.lineTo(Math.cos(a1 + 0.9) * R * 0.2, Math.sin(a1 + 0.9) * R * 0.2);
    cx.lineTo(Math.cos(a2) * R * 0.84, Math.sin(a2) * R * 0.84);
    cx.stroke();
  }

  // 4. crateres: la cantidad es igual a las soluciones que faltan
  for (let i = 0; i < r.stages; i++) {
    const ang = 1.1 + i * 2.2;
    const cr = R * (0.22 - i * 0.03);
    const cxp = Math.cos(ang) * R * 0.42;
    const cyp = Math.sin(ang) * R * 0.42;
    cx.beginPath();
    cx.arc(cxp, cyp, cr, 0, 6.283);
    cx.fillStyle = "rgba(0,0,0,0.5)";
    cx.fill();
    cx.beginPath();
    cx.arc(cxp, cyp, cr, Math.PI * 1.15, Math.PI * 1.95);
    cx.strokeStyle = `hsla(${baseH},26%,62%,0.5)`;
    cx.lineWidth = 1.1;
    cx.stroke();
  }

  // 5. silueta con destello
  rockPath(cx, r.verts, R);
  cx.strokeStyle = rim;
  cx.lineWidth = (1.0 + 1.2 * p) * (isCand ? 1.8 : 1);
  cx.shadowColor = rim;
  cx.shadowBlur = (4 + 10 * p) * (isCand ? 1.9 : 1);
  cx.stroke();
  cx.shadowBlur = 0;

  // 6. luz de canto
  cx.beginPath();
  cx.arc(0, 0, R * 0.94, Math.PI * 1.08, Math.PI * 1.82);
  cx.strokeStyle = `hsla(${baseH},34%,84%,0.5)`;
  cx.lineWidth = 1.4;
  cx.stroke();
  cx.restore();

  if (isCand) {
    cx.strokeStyle = C.hot;
    cx.globalAlpha = 0.5;
    cx.lineWidth = 0.9;
    cx.beginPath();
    cx.arc(r.x, r.y, R + 10 * sc, 0, 6.283);
    cx.stroke();
    cx.globalAlpha = 1;
  }

  // etiqueta: sin destello, sobre placa opaca, peso 700.
  // El destello sobre digitos chicos se come los contraformas del 6, 8 y 9,
  // asi que el brillo se queda en la silueta y el texto va limpio.
  const fs = Math.round(15 + 5 * Math.min(1, p));
  const txt = chipping ? r.txt.replace(/\d/g, () => String(ri(10))) : r.txt;
  cx.font = `700 ${fs}px ${FONT}`;
  cx.textAlign = "center";
  cx.textBaseline = "middle";
  const tw = cx.measureText(txt).width;
  const bw = tw + 16;
  const bh = fs + 10;
  const by = r.y + R + bh * 0.7;
  cx.fillStyle = "rgba(2,5,8,0.94)";
  roundRect(cx, r.x - bw / 2, by - bh / 2, bw, bh, 2);
  cx.fill();
  cx.strokeStyle = isCand ? C.hot : chipping ? C.accent : "rgba(30,122,92,0.85)";
  cx.lineWidth = 1;
  cx.stroke();
  cx.fillStyle = isCand ? C.hot : chipping ? C.accent : C.hot;
  setTracking(cx, "0px");
  cx.fillText(txt, r.x, by + 0.5);
}
